/**
 * Utility for managing activity examples
 * - localStorage: Temporary preview data (auto-save while filling)
 * - Backend API: Permanent saved examples
 */

import { config } from '../Environment';

const STORAGE_KEY_PREFIX = 'activity_example_';
const TEMP_DATA_KEY = 'activity_temp_data_';

/**
 * Get all examples for an activity
 * @param {string} activityId - The activity/prescription ID
 * @returns {Array} Array of examples
 */
export const getActivityExamples = (activityId) => {
  try {
    const key = `${STORAGE_KEY_PREFIX}${activityId}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading examples:', error);
    return [];
  }
};

/**
 * Save a new example for an activity
 * @param {string} activityId - The activity/prescription ID
 * @param {object} exampleData - The example data
 * @param {string} name - Name for the example
 * @returns {object} The saved example
 */
export const saveActivityExample = (activityId, exampleData, name) => {
  try {
    const examples = getActivityExamples(activityId);
    const newExample = {
      id: `example_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: name || `Example ${examples.length + 1}`,
      data: exampleData,
      createdAt: new Date().toISOString(),
    };

    examples.push(newExample);
    const key = `${STORAGE_KEY_PREFIX}${activityId}`;
    localStorage.setItem(key, JSON.stringify(examples));

    return newExample;
  } catch (error) {
    console.error('Error saving example:', error);
    throw error;
  }
};

/**
 * Delete an example
 * @param {string} activityId - The activity/prescription ID
 * @param {string} exampleId - The example ID to delete
 */
export const deleteActivityExample = (activityId, exampleId) => {
  try {
    const examples = getActivityExamples(activityId);
    const filtered = examples.filter(ex => ex.id !== exampleId);
    const key = `${STORAGE_KEY_PREFIX}${activityId}`;
    localStorage.setItem(key, JSON.stringify(filtered));
  } catch (error) {
    console.error('Error deleting example:', error);
    throw error;
  }
};

/**
 * Get temporary preview data (persisted across preview sessions)
 * @param {string} activityId - The activity/prescription ID
 * @returns {object|null} Temporary data or null
 */
export const getTempPreviewData = (activityId) => {
  try {
    const key = `${TEMP_DATA_KEY}${activityId}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error loading temp data:', error);
    return null;
  }
};

/**
 * Save temporary preview data (auto-saved as user fills the preview)
 * @param {string} activityId - The activity/prescription ID
 * @param {object} data - The preview data to save
 */
export const saveTempPreviewData = (activityId, data) => {
  try {
    const key = `${TEMP_DATA_KEY}${activityId}`;
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving temp data:', error);
  }
};

/**
 * Clear temporary preview data
 * @param {string} activityId - The activity/prescription ID
 */
export const clearTempPreviewData = (activityId) => {
  try {
    const key = `${TEMP_DATA_KEY}${activityId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing temp data:', error);
  }
};

/**
 * Update an existing example
 * @param {string} activityId - The activity/prescription ID
 * @param {string} exampleId - The example ID to update
 * @param {object} updates - Updated properties
 */
export const updateActivityExample = (activityId, exampleId, updates) => {
  try {
    const examples = getActivityExamples(activityId);
    const index = examples.findIndex(ex => ex.id === exampleId);

    if (index !== -1) {
      examples[index] = {
        ...examples[index],
        ...updates,
        updatedAt: new Date().toISOString(),
      };
      const key = `${STORAGE_KEY_PREFIX}${activityId}`;
      localStorage.setItem(key, JSON.stringify(examples));
      return examples[index];
    }

    return null;
  } catch (error) {
    console.error('Error updating example:', error);
    throw error;
  }
};

// ====================================
// Backend API Functions (Process-based)
// ====================================

/**
 * Save example to backend as a process with isExample flag
 * @param {string} prescriptionId - The prescription/activity ID
 * @param {object} exampleData - The example data { userData, notes }
 * @param {string} name - Name for the example
 * @param {string} description - Description for the example
 * @param {object} stepStack - Navigation history
 * @param {string} nextActivity - Current activity position
 * @param {string} prescriptionHistoryId - Prescription history ID
 * @param {string} prologue - Prologue text
 * @param {string} epilogue - Epilogue text
 * @param {function} retryFetch - The retryFetch function from useRetryFetch hook
 * @returns {Promise<object>} The saved example process from backend
 */
export const saveExampleToBackend = async (
  prescriptionId,
  exampleData,
  name,
  description,
  stepStack,
  nextActivity,
  prescriptionHistoryId,
  prologue,
  epilogue,
  retryFetch
) => {
  try {
    const response = await retryFetch(
      `${config.API_BASE}/api/processes/${prescriptionHistoryId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          isExample: true,
          exampleName: name,
          exampleDescription: description || '',
          isPublished: false,
          stepStack: stepStack,
          nextActivity: nextActivity,
          notes: exampleData.notes || {},
          prologue: prologue || '',
          epilogue: epilogue || '',
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('Failed to save example - Response:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });
      throw new Error(errorData.error || `Failed to save example: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error saving example to backend:', error);
    throw error;
  }
};

/**
 * Fetch examples from backend (processes with isExample flag)
 * @param {string} prescriptionId - The prescription/activity ID
 * @param {function} retryFetch - The retryFetch function from useRetryFetch hook
 * @returns {Promise<Array>} Array of examples
 */
export const fetchExamplesFromBackend = async (prescriptionId, retryFetch) => {
  try {
    const response = await retryFetch(
      `${config.API_BASE}/api/prescriptions/${prescriptionId}/examples`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch examples: ${response.status}`);
    }

    const result = await response.json();

    if (result.status === 'LIST') {
      return result.examples;
    } else {
      throw new Error(result.error || 'Failed to fetch examples');
    }
  } catch (error) {
    console.error('Error fetching examples from backend:', error);
    throw error;
  }
};

/**
 * Delete example from backend (delete process)
 * @param {string} processId - The process/example ID to delete
 * @param {function} retryFetch - The retryFetch function from useRetryFetch hook
 * @returns {Promise<void>}
 */
export const deleteExampleFromBackend = async (processId, retryFetch) => {
  try {
    const response = await retryFetch(
      `${config.API_BASE}/api/examples/${processId}`,
      {
        method: 'DELETE',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error('Failed to delete example');
    }
  } catch (error) {
    console.error('Error deleting example from backend:', error);
    throw error;
  }
};

/**
 * Publish an example (make visible to users)
 * @param {string} processId - The process/example ID to publish
 * @param {function} retryFetch - The retryFetch function from useRetryFetch hook
 * @param {boolean} isCreatorAnonymous - Whether to publish anonymously (default: false)
 * @returns {Promise<object>} Updated example
 */
export const publishExampleToBackend = async (processId, retryFetch, isCreatorAnonymous = false) => {
  try {
    const response = await retryFetch(
      `${config.API_BASE}/api/processes/${processId}/publish`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          isCreatorAnonymous: isCreatorAnonymous === true,
        }),
      }
    );

    const result = await response.json();

    if (result.status === 'PUBLISHED') {
      return result.example;
    } else {
      throw new Error(result.error || 'Failed to publish example');
    }
  } catch (error) {
    console.error('Error publishing example:', error);
    throw error;
  }
};

/**
 * Unpublish an example (hide from users)
 * @param {string} processId - The process/example ID to unpublish
 * @param {function} retryFetch - The retryFetch function from useRetryFetch hook
 * @returns {Promise<object>} Updated example
 */
export const unpublishExampleFromBackend = async (processId, retryFetch) => {
  try {
    const response = await retryFetch(
      `${config.API_BASE}/api/processes/${processId}/unpublish`,
      {
        method: 'PATCH',
        credentials: 'include',
      }
    );

    const result = await response.json();

    if (result.status === 'UNPUBLISHED') {
      return result.example;
    } else {
      throw new Error(result.error || 'Failed to unpublish example');
    }
  } catch (error) {
    console.error('Error unpublishing example:', error);
    throw error;
  }
};

/**
 * Submit an example for approval (regular users)
 * @param {string} processId - The process/engagement ID to submit
 * @param {string} exampleName - Name for the example
 * @param {string} exampleDescription - Description for the example
 * @param {string} prologue - Prologue text
 * @param {string} epilogue - Epilogue text
 * @param {object} notes - Notes object
 * @param {boolean} isCreatorAnonymous - Whether to submit anonymously
 * @param {function} retryFetch - The retryFetch function from useRetryFetch hook
 * @returns {Promise<object>} Submitted example
 */
export const submitExampleForApproval = async (
  processId,
  exampleName,
  exampleDescription,
  prologue,
  epilogue,
  notes,
  isCreatorAnonymous,
  retryFetch
) => {
  try {
    const response = await retryFetch(
      `${config.API_BASE}/api/processes/${processId}/submit-example`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          exampleName,
          exampleDescription: exampleDescription || '',
          prologue: prologue || '',
          epilogue: epilogue || '',
          notes: notes || {},
          isCreatorAnonymous: isCreatorAnonymous === true,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to submit example');
    }

    const result = await response.json();
    return result.example;
  } catch (error) {
    console.error('Error submitting example for approval:', error);
    throw error;
  }
};

/**
 * Update example metadata in backend
 * Only the activity author can update examples
 * @param {string} processId - The process/example ID to update
 * @param {string} exampleName - Updated name for the example
 * @param {string} exampleDescription - Updated description for the example
 * @param {string} prologue - Updated prologue text
 * @param {string} epilogue - Updated epilogue text
 * @param {object} notes - Updated notes object
 * @param {array} stepStack - Updated stepStack array (optional)
 * @param {function} retryFetch - The retryFetch function from useRetryFetch hook
 * @returns {Promise<object>} Updated example
 */
export const updateExampleInBackend = async (
  processId,
  exampleName,
  exampleDescription,
  prologue,
  epilogue,
  notes,
  stepStack,
  retryFetch
) => {
  try {
    const requestBody = {
      exampleName,
      exampleDescription: exampleDescription || '',
      prologue: prologue || '',
      epilogue: epilogue || '',
      notes: notes || {},
    };

    // Only include stepStack if provided
    if (stepStack !== undefined) {
      requestBody.stepStack = stepStack;
    }

    const response = await retryFetch(
      `${config.API_BASE}/api/examples/${processId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update example');
    }

    const result = await response.json();
    return result.example;
  } catch (error) {
    console.error('Error updating example in backend:', error);
    throw error;
  }
};
